import { useState, useEffect, useCallback } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { useScroll } from '../hooks/useScroll'
import LoginModal from './LoginModal'
import { useAuth } from '../contexts/AuthContext'
import UserDropdown from './UserDropdown'
import NotificationIcon from './NotificationIcon'
import './Navbar.css'

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isLoginOpen, setIsLoginOpen] = useState(false)
  const { isScrolled } = useScroll()
  const { user, login } = useAuth()
  const navigate = useNavigate()

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen)
  }

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false)
  }, [])

  const handleLoginClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsLoginOpen(true)
  }, [])

  const handleLoginClose = useCallback(() => {
    setIsLoginOpen(false)
  }, [])

  const handleLoginSuccess = useCallback((userData: { email: string; name: string; username: string; id: string }) => {
    login(userData)
    setIsLoginOpen(false)
    // Redirect to community page after login
    navigate('/community')
  }, [login, navigate])

  // Close menu on outside click
  useEffect(() => {
    if (!isMenuOpen) return

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      // Don't close if clicking on UserDropdown or its menu
      if (target.closest('.user-dropdown') || target.closest('.user-dropdown-menu')) {
        return
      }
      if (!target.closest('.main-navigation') && !target.closest('.nav-toggle')) {
        closeMenu()
      }
    }

    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [isMenuOpen, closeMenu])

  // Close menu when login opens
  useEffect(() => {
    if (isLoginOpen) {
      setIsMenuOpen(false)
    }
  }, [isLoginOpen])

  return (
    <header className={`site-header ${isScrolled ? 'scrolled' : ''}`}>
      <div className="header-container">
        <div className="logo">
          <NavLink 
            to="/" 
            aria-label="Nessa Kodo Home"
            onClick={(e) => {
              if (window.location.pathname === '/') {
                e.preventDefault()
                window.scrollTo({ top: 0, behavior: 'smooth' })
              }
            }}
          >
            <div className="logo-placeholder">
              <img src="/images/logo.png" alt="Nessa Kodo Logo" />
            </div>
          </NavLink>
        </div>
        <button
          className={`nav-toggle ${isMenuOpen ? 'is-open' : ''}`}
          aria-label="Toggle navigation"
          aria-expanded={isMenuOpen}
          onClick={toggleMenu}
        >
          <span></span>
          <span></span>
        </button>
        <nav
          className={`main-navigation ${isMenuOpen ? 'open' : ''}`}
          role="navigation"
          aria-label="Main navigation"
        >
          <ul className="nav-menu">
            <li>
              <NavLink to="/sessions" onClick={closeMenu} className={({ isActive }) => isActive ? 'active' : ''}>
                sessions
              </NavLink>
            </li>
            <li>
              <NavLink to="/about" onClick={closeMenu} className={({ isActive }) => isActive ? 'active' : ''}>
                about
              </NavLink>
            </li>
            <li>
              <NavLink to="/library" onClick={closeMenu} className={({ isActive }) => isActive ? 'active' : ''}>
                library
              </NavLink>
            </li>
            {user && (
              <li>
                <NavLink to="/community" onClick={closeMenu} className={({ isActive }) => isActive ? 'active' : ''}>
                  community
                </NavLink>
              </li>
            )}
          </ul>
          <div className="auth-actions" id="auth-actions">
            {user && <NotificationIcon />}
            {user ? (
              <UserDropdown onNavigate={closeMenu} />
            ) : (
              <button 
                className="auth-button login-button" 
                onClick={handleLoginClick}
                type="button"
                aria-label="Login"
              >
                <i className="fas fa-user"></i>
              </button>
            )}
          </div>
        </nav>
      </div>
      <LoginModal
        isOpen={isLoginOpen}
        onClose={handleLoginClose}
        onLoginSuccess={handleLoginSuccess}
      />
    </header>
  )
}

export default Navbar
